import { useCallback } from 'react'
import {
  supplyListingPosted as fireSupplyListingPostedPixel,
  SupplyFunnel,
  supplyLead,
  supplyLeadStart,
  supplyView
} from 'src/utils/pixels/spotmeEvents'
import useTrackEvent from './useTrackEvent'

type SupplyEventArgs = {
  funnel: SupplyFunnel
  /** Surface label inside the funnel (e.g. `hero` | `final_cta` | `whatsapp`).
   *  Lets marketing slice within a funnel without splitting the event name. */
  source?: string
  /** Page path override — defaults to `window.location.pathname`. */
  pagePath?: string
}

type SupplyListingPostedArgs = {
  locationId: string
  /** Page path override — defaults to `window.location.pathname`. */
  pagePath?: string
  /** How the listing was published (e.g. `photo_fast` = «Revisa y publica»).
   *  Pixel only — the internal ES event has no source field. */
  source?: string
}

/**
 * Single hook for /anfitrion/* surfaces to fire the full supply-side
 * conversion stack:
 *
 *   pixel layer       → GA4 / Meta `SupplyLead` / Bing UET / LinkedIn / GTM
 *   internal layer    → `SUPPLY_LEAD_START` / `SUPPLY_LEAD` /
 *                       `SUPPLY_LISTING_POSTED` server event
 *                       (pillar=host, subType=funnel) for the marketing report
 *
 * Strictly separate from the demand-side `generate_lead` family — supply
 * pages MUST use these helpers, not `generateLead`. Demand pages MUST NOT
 * call any of these. See [`spotmeEvents.ts`](../utils/pixels/spotmeEvents.ts)
 * for the full rationale.
 */
const useSupplyLead = () => {
  const { trackEvent, EventAction } = useTrackEvent()

  const fireView = useCallback(
    ({ funnel, source, pagePath }: SupplyEventArgs) => {
      supplyView({ funnel, source, pagePath })
    },
    []
  )

  const fireLeadStart = useCallback(
    ({ funnel, source, pagePath }: SupplyEventArgs) => {
      supplyLeadStart({ funnel, source, pagePath })
      trackEvent({
        action: EventAction.SupplyLeadStart,
        subType: funnel,
        pillar: 'host',
        pagePath:
          pagePath ??
          (typeof window !== 'undefined' ? window.location.pathname : undefined)
      })
    },
    [trackEvent, EventAction]
  )

  const fireLead = useCallback(
    ({ funnel, source, pagePath }: SupplyEventArgs) => {
      supplyLead({ funnel, source, pagePath })
      trackEvent({
        action: EventAction.SupplyLead,
        subType: funnel,
        pillar: 'host',
        pagePath:
          pagePath ??
          (typeof window !== 'undefined' ? window.location.pathname : undefined)
      })
    },
    [trackEvent, EventAction]
  )

  // Terminal supply conversion — fires once per actually-published listing
  // from AddPostFlow Success step. Belt-and-suspenders: client fire here,
  // server-side mirror at Location create (see spotme-server
  // LocationService). Either path reaching ES is sufficient.
  const fireListingPosted = useCallback(
    ({ locationId, pagePath, source }: SupplyListingPostedArgs) => {
      fireSupplyListingPostedPixel({ pagePath, source })
      trackEvent({
        action: EventAction.SupplyListingPosted,
        locationId,
        subType: 'add_post',
        pillar: 'host',
        pagePath:
          pagePath ??
          (typeof window !== 'undefined' ? window.location.pathname : undefined)
      })
    },
    [trackEvent, EventAction]
  )

  return {
    supplyView: fireView,
    supplyLeadStart: fireLeadStart,
    supplyLead: fireLead,
    supplyListingPosted: fireListingPosted
  }
}

export default useSupplyLead
